export const PERMISSIONS = {
  network_admin: ["*"],
  studio_owner: [
    "customers:read", "customers:write", "customers:delete", "jobs:read", "jobs:write", "jobs:delete",
    "quotes:read", "quotes:write",
    "interactions:read", "interactions:write", "stock:read", "stock:write", "reports:read",
    "audit:read", "users:read", "users:write", "studios:read",
    "reminders:read", "reminders:write", "scheduling:read", "scheduling:write",
    "alerts:read", 
    "catalog:read", "orders:read", "orders:write", "orders:manage", "config:read",
  ],
  studio_manager: [
    "customers:read", "customers:write", "customers:delete", "jobs:read", "jobs:write", "jobs:delete",
    "quotes:read", "quotes:write",
    "interactions:read", "interactions:write", "stock:read", "stock:write", "reports:read",
    "users:read", "studios:read",
    "reminders:read", "reminders:write", "scheduling:read", "scheduling:write",
    "alerts:read", "catalog:read", "orders:read", "orders:write",
    "orders:manage", "config:read",
  ],
  content_manager: ["studios:read", "config:read", "performance:me"],
  central_marketing: ["studios:read", "config:read", "performance:me", "alerts:read", "targets:read", "bc:read", "bc:respond"],
  central_sales_executive: ["b2b:read", "b2b:write", "reminders:read", "reminders:write", "interactions:read", "interactions:write", "catalog:read", "studios:read", "config:read", "performance:me", "targets:read"],
  brand_consultant: ["studios:read", "config:read", "bc:read", "bc:write", "bc:message", "bc:metrics"],
  installer: [],
};

export const CENTRAL_ROLES = [
  "sales_manager_products",
  "central_sales_executive",
  "crm",
  "accountant",
  "central_marketing",
  "brand_consultant",
  "ceo",
];

export const ROLE_LABELS = {
  network_admin: "Admin",
  ceo: "CEO",
  studio_investor: "Studio Investor",
  sales_manager_products: "Sales Manager - Products",
  central_sales_executive: "Central Sales Executive (Products)",
  central_sales_team: "Central Sales Team B2C",
  crm: "CRM",
  accountant: "Accountant",
  central_marketing: "Central Marketing Manager",
  brand_consultant: "Brand Consultant",
  studio_owner: "Studio Owner",
  studio_manager: "Studio Manager",
  content_manager: "Content Manager",
  installer: "PPF Installer",
};

export const PIPELINE = [
  "Lead",
  "Quoted",
  "Booked",
  "In Progress",
  "Delivered",
  "Follow-up",
  "Repeat/Referral",
];

export const LEAD_SOURCES = [
  "Customer Referral",
  "Central Sales Team",
  "Instagram",
  "Google Map",
  "Walk-in",
  "Other",
];

export const INTERACTION_TYPES = ["Call", "WhatsApp", "In-Person", "DM"];
export const INTERACTION_OUTCOMES = [
  "Answered – Interested",
  "Answered – Not interested",
  "No answer / Missed",
  "Call back later",
  "Busy",
  "Wrong / invalid number",
  "Booked / Converted",
  "Info shared",
];

// Early pipeline stages where lead details stay optional. From "Booked" onward they're required.
export const EARLY_STAGES = ["Lead", "Intersted", "Follow up", "Not Interested"];
export const isCommittedStage = (status, stages = []) => {
  if (EARLY_STAGES.includes(status)) return false;
  const bi = stages.indexOf("Booked");
  const si = stages.indexOf(status);
  if (bi >= 0 && si >= 0) return si >= bi;
  return true;
};
export const isCentralRole = (role) => ["central_sales_team"].includes(role);

export const can = (user, perm) => {
  if (!user) return false;
  // the server resolves built-in and custom roles; fall back to the static map
  const perms = user.permissions?.length ? user.permissions : PERMISSIONS[user.role] || [];
  return perms.includes("*") || perms.includes(perm);
};

export const roleLabel = (user) => user?.role_label || ROLE_LABELS[user?.role] || user?.role;

// A job/customer belongs to the Central Sales Team if a CS person is attributed or the lead
// source is Central Sales.
export const recordIsCentral = (rec) =>
  !!rec?.cs_person_id || (rec?.lead_source || "").toLowerCase().includes("central sales");

// Mirror of the backend ownership rules. Only studio roles and Central Sales Team B2C are
// restricted; every other role with the permission passes through (backend still enforces).
export const canDeleteRecord = (user, rec, perm) => {
  if (!user || !rec || !can(user, perm)) return false;
  const role = user.role;
  const uid = user.id;
  if (role === "central_sales_team")
    return recordIsCentral(rec) && (rec.cs_person_id === uid || rec.created_by_id === uid);
  if (role === "studio_owner" || role === "studio_manager")
    return !recordIsCentral(rec) && !!rec.studio_id && rec.studio_id === user.studio_id;
  return true;
};

// Editing rules (backend still enforces). By default studios may edit only their own studio's
// NON-central records. `allowStudioCentral` (jobs only) lets the assigned studio edit a
// Central Sales job at their studio — ownership/attribution never transfers.
export const canEditRecord = (user, rec, perm, { allowStudioCentral = false } = {}) => {
  if (!user || !rec || !can(user, perm)) return false;
  const role = user.role;
  const uid = user.id;
  if (role === "central_sales_team")
    return recordIsCentral(rec) && (rec.cs_person_id === uid || rec.created_by_id === uid);
  if (role === "studio_owner" || role === "studio_manager") {
    if (!rec.studio_id || rec.studio_id !== user.studio_id) return false;
    return allowStudioCentral || !recordIsCentral(rec);
  }
  return true;
};
